import { useCallback, useEffect, useState } from 'react';
import {
  AppBar,
  Box,
  Button,
  Container,
  IconButton,
  Skeleton,
  Alert,
  Toolbar,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import publicationService from '../services/publicationService';
import { UserPublication } from '../types/publication.types';
import { PublicationCard } from '../components/PublicationCard';

const PAGE_SIZE = 10;

function CardSkeleton() {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: 'background.paper',
        boxShadow: 1,
      }}
    >
      <Skeleton
        variant="rectangular"
        width={90}
        height={90}
        sx={{ borderRadius: 2, flexShrink: 0 }}
      />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width="70%" height={24} />
        <Skeleton variant="text" width="50%" height={20} />
        <Skeleton variant="text" width="60%" height={20} />
        <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 4, mt: 0.5 }} />
      </Box>
    </Box>
  );
}

export function MyPublicationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [publications, setPublications] = useState<UserPublication[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadPage = useCallback(
    async (pageNum: number, append: boolean) => {
      try {
        if (append) {
          setLoadingMore(true);
        } else {
          setLoading(true);
        }
        const result = await publicationService.getUserPublications({
          page: pageNum,
          limit: PAGE_SIZE,
        });
        setPublications(prev => (append ? [...prev, ...result.data] : result.data));
        setTotal(result.meta.total);
        setError(null);
      } catch (err) {
        console.error('Failed to load publications:', err);
        setError(t('myPublications.errorLoading'));
      } finally {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [t]
  );

  useEffect(() => {
    loadPage(1, false);
  }, [loadPage]);

  const handleSeeMore = () => {
    const next = page + 1;
    setPage(next);
    loadPage(next, true);
  };

  const hasMore = publications.length < total;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Fixed header */}
      <AppBar
        position="fixed"
        sx={{
          bgcolor: 'primary.main',
          borderRadius: '0 0 16px 16px',
          zIndex: theme => theme.zIndex.appBar,
          paddingTop: 'env(safe-area-inset-top)',
        }}
        data-testid="publications-history-header"
      >
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/', { replace: true })}
            aria-label="back"
            data-testid="publications-history-back"
          >
            <ArrowBack />
          </IconButton>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: 1,
              justifyContent: 'center',
              mr: 5,
            }}
          >
            <Typography variant="h6" component="div" sx={{ color: 'white', fontWeight: 600 }}>
              {t('myPublications.historyTitle')}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Scrollable content, offset below fixed AppBar */}
      <Container maxWidth="md" sx={{ pt: 2, pb: 10, px: 2 }}>
        {/* Initial loading */}
        {loading && (
          <Box
            sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
            data-testid="publications-history-loading"
          >
            {Array.from({ length: 5 }).map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </Box>
        )}

        {/* Error */}
        {!loading && error && (
          <Alert severity="error" data-testid="publications-history-error">
            {error}
          </Alert>
        )}

        {/* Empty */}
        {!loading && !error && publications.length === 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            data-testid="publications-history-empty"
          >
            {t('myPublications.noPublications')}
          </Typography>
        )}

        {/* List */}
        {!loading && !error && publications.length > 0 && (
          <Box data-testid="publications-history-list">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {publications.map(pub => (
                <PublicationCard key={pub.id} publication={pub} />
              ))}
            </Box>

            {/* Load more skeletons while fetching next page */}
            {loadingMore && (
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}
                data-testid="publications-history-loading-more"
              >
                {Array.from({ length: 3 }).map((_, i) => (
                  <CardSkeleton key={i} />
                ))}
              </Box>
            )}

            {/* See more button */}
            {hasMore && !loadingMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleSeeMore}
                  data-testid="publications-history-see-more"
                  sx={{ textTransform: 'none', borderRadius: 3, px: 4 }}
                >
                  {t('myPublications.seeMore')}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
}
